import "../../global.css";
import { useEffect, useState } from "react";
import { userRequest } from "../../requestMethods";
import { useCompany } from "../../components/context/CompanyContext";
import CustomGrid from "../../components/grid/CustomGrid";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Swal from "sweetalert2";
import Loading from "../../components/loading/loading";

const EMPTY_FORM = { paymentType: "", enabled: true };

export default function PaymentTypeList() {
  const { companyId } = useCompany();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null); // paymentTypeId being edited

  useEffect(() => {
    if (companyId > 0) getPaymentTypes();
  }, [companyId]);

  const getPaymentTypes = async () => {
    try {
      setIsLoading(true);
      const res = await userRequest.get(`${companyId}/paymentTypes`);
      setPaymentTypes(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.paymentType.trim()) {
      Swal.fire("Atenção!", "Informe o nome do tipo de pagamento.", "warning");
      return;
    }

    try {
      setIsLoading(true);
      if (editing) {
        await userRequest.put(
          `${companyId}/paymentTypes/${editing}`,
          { paymentType: form.paymentType, enabled: form.enabled },
        );
        Swal.fire("Sucesso!", "Tipo de pagamento atualizado.", "success");
      } else {
        await userRequest.post(
          `${companyId}/paymentTypes`,
          { paymentType: form.paymentType, enabled: form.enabled },
        );
        Swal.fire("Sucesso!", "Tipo de pagamento cadastrado.", "success");
      }
      setForm(EMPTY_FORM);
      setEditing(null);
      getPaymentTypes();
    } catch (e) {
      Swal.fire("Erro!", "Não foi possível salvar.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (row) => {
    if (row.companyId === 0) return;
    setEditing(row.paymentTypeId);
    setForm({ paymentType: row.paymentType, enabled: row.enabled });
  };

  // Tipo padrão (companyId 0) é compartilhado com as outras empresas: aqui só
  // ligamos/desligamos para ESTA empresa — o backend grava um override e nunca
  // altera a linha global.
  const handleToggleEnabled = async (row) => {
    const enabled = !row.enabled;
    const body =
      row.companyId === 0
        ? { enabled }
        : { paymentType: row.paymentType, enabled };

    setPaymentTypes((prev) =>
      prev.map((item) =>
        item.paymentTypeId === row.paymentTypeId ? { ...item, enabled } : item,
      ),
    );

    try {
      await userRequest.put(`${companyId}/paymentTypes/${row.paymentTypeId}`, body);
    } catch (e) {
      setPaymentTypes((prev) =>
        prev.map((item) =>
          item.paymentTypeId === row.paymentTypeId
            ? { ...item, enabled: row.enabled }
            : item,
        ),
      );
      Swal.fire(
        "Erro!",
        e?.response?.data?.message || "Não foi possível alterar o status.",
        "error",
      );
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
  };

  const columns = [
    {
      field: "paymentTypeId",
      type: "number",
      headerName: "Código",
      width: 80,
      headerClassName: "dataGridMuiHeader",
    },
    {
      field: "paymentType",
      type: "text",
      headerName: "Tipo de Pagamento",
      width: 250,
      headerClassName: "dataGridMuiHeader",
    },
    {
      field: "enabled",
      type: "boolean",
      headerName: "Ativo",
      width: 80,
      headerClassName: "dataGridMuiHeader",
      renderCell: (params) => (
        <Switch
          checked={!!params.row.enabled}
          onChange={() => handleToggleEnabled(params.row)}
          size="small"
        />
      ),
    },
    {
      field: "companyId",
      type: "number",
      headerName: "Origem",
      width: 120,
      headerClassName: "dataGridMuiHeader",
      renderCell: (params) =>
        params.row.companyId === 0 ? (
          <span style={{ color: "#888", fontSize: 12 }}>Padrão</span>
        ) : (
          <span style={{ color: "#EE8253", fontSize: 12 }}>Empresa</span>
        ),
    },
    {
      field: "editar",
      type: "text",
      headerName: "",
      width: 60,
      headerClassName: "dataGridMuiHeader",
      renderCell: (params) =>
        params.row.companyId !== 0 ? (
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <div className="cust-page">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="cust-title">Tipos de Pagamento</div>

          <div className="cust-card">
            <div className="cust-section-header">
              {editing ? "Editar Tipo de Pagamento" : "Novo Tipo de Pagamento"}
            </div>
            <div className="cust-fields cust-fields-3">
              <div className="cust-field">
                <label className="cust-label">Nome</label>
                <input
                  className="cust-input"
                  type="text"
                  name="paymentType"
                  value={form.paymentType}
                  onChange={handleChange}
                  maxLength={100}
                />
              </div>
              <div className="cust-field" style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 20 }}>
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                  size="small"
                />
                <span className="cust-label">{form.enabled ? "Ativo" : "Inativo"}</span>
              </div>
            </div>
            <div style={{ padding: "0 16px 16px", display: "flex", gap: 8 }}>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                {editing ? "Salvar Alteração" : "Adicionar"}
              </Button>
              {editing && (
                <Button variant="outlined" size="small" onClick={handleCancel}>
                  Cancelar
                </Button>
              )}
            </div>
          </div>

          <div className="cust-card">
            <div className="cust-section-header">Tipos Cadastrados</div>
            <div style={{ padding: 16 }}>
              <CustomGrid
                gridId="paymentTypeList"
                rows={paymentTypes}
                columns={columns}
                pageSize={50}
                checkboxSelection={false}
                getRowId={(row) => row.paymentTypeId}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
